import { useGuttagekonto } from '@/domain/mitarbeiter/features/guttagekonto/hooks/useGuttagekonto';
import { mapAbwesenheitsgrund } from '@/domain/mitarbeiter/features/guttagekonto/shared/entities/Abwesenheitsgruende';
import {
  calculateSumDauer,
  calculateSumFaktor,
} from '@/domain/mitarbeiter/features/guttagekonto/utils/calculateSum';
import { formatMinutes } from '@/domain/mitarbeiter/features/guttagekonto/utils/formatMinutes';
import { formatNumberToString } from '@/domain/mitarbeiter/features/guttagekonto/utils/formatNumberToString';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@e2n/ui/v2';
import {
  faCalendarXmark,
  faMessageLines,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

export function AbwesenheitenTable() {
  const { id } = useParams();
  const currentYear = dayjs().year();
  const { guttagekonto } = useGuttagekonto([id], currentYear);
  const abwesenheiten = guttagekonto.abwesenheiten;
  const { t } = useTranslation();

  let totalSumOfFaktor = abwesenheiten
    ? calculateSumFaktor({ abwesenheiten: abwesenheiten })
    : 0;
  let totalSumOfDauer = abwesenheiten
    ? calculateSumDauer({ abwesenheiten: abwesenheiten })
    : 0;

  return (
    <>
      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead sorted="none">
                {t('domain.mitarbeiter.guttagekonto.datum')}
              </TableHead>
              <TableHead sorted="none">
                {t('domain.mitarbeiter.guttagekonto.grund')}
              </TableHead>
              <TableHead sorted="none">
                {t('domain.mitarbeiter.guttagekonto.tage')}
              </TableHead>
              <TableHead sorted="none">
                {t('domain.mitarbeiter.guttagekonto.dauer')}
              </TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {guttagekonto.abwesenheiten ? (
              <>
                {guttagekonto.abwesenheiten.map((abwesenheit, index) => (
                  <TableRow key={index}>
                    <TableCell
                      type="text"
                      text={dayjs(abwesenheit.datum).format('dd., DD.MM.')}
                      showBottomLine={
                        index <= guttagekonto.abwesenheiten.length - 1
                      }></TableCell>

                    <TableCell type="badge">
                      <Badge
                        layout="character"
                        size="md"
                        icon={
                          <FontAwesomeIcon
                            color={abwesenheit.grund.markierung}
                            icon={abwesenheit.abwesenheitsgrundIcon}
                          />
                        }>
                        {mapAbwesenheitsgrund(abwesenheit.grund.value)}
                      </Badge>
                    </TableCell>

                    <TableCell
                      type="text"
                      text={formatNumberToString(abwesenheit.faktor)}
                      showBottomLine={
                        index <= guttagekonto.abwesenheiten.length - 1
                      }
                    />

                    <TableCell
                      type="text"
                      text={formatMinutes(abwesenheit.dauer)}
                      showBottomLine={
                        index <= guttagekonto.abwesenheiten.length - 1
                      }
                    />
                    <TableCell
                      type="actions"
                      showBottomLine={
                        index < guttagekonto.abwesenheiten.length - 1
                      }>
                      {abwesenheit.bemerkung && (
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button variant="ghost" iconOnly aria-label="Info">
                              <FontAwesomeIcon icon={faMessageLines} />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent>
                            <PopoverBody>
                              <p className="text-sm leading-5 text-labels-primary">
                                {abwesenheit.bemerkung}
                              </p>
                            </PopoverBody>
                          </PopoverContent>
                        </Popover>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell type="text" text="Gesamt" showBottomLine={false} />
                  <TableCell showBottomLine={false}></TableCell>
                  <TableCell
                    type="text"
                    text={formatNumberToString(totalSumOfFaktor)}
                    showBottomLine={false}
                  />
                  <TableCell
                    type="text"
                    text={formatMinutes(totalSumOfDauer)}
                    showBottomLine={false}
                  />
                  <TableCell showBottomLine={false}></TableCell>
                </TableRow>
              </>
            ) : (
              <>
                <TableRow>
                  <TableCell
                    type="actions"
                    showBottomLine={false}
                    colSpan={5}
                    className="p-8 align-middle">
                    <EmptyState
                      title="Keine Abwesenheit vorhanden."
                      description="Im Jahr 2026 wurden keine Guttage erfasst."
                      icon={<FontAwesomeIcon icon={faCalendarXmark} />}
                    />
                  </TableCell>
                </TableRow>
              </>
            )}
          </TableBody>
        </Table>
      </Card>
    </>
  );
}
